import React, { CSSProperties, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

type StylesType = Record<string, CSSProperties>;

// Inject keyframes into document head to avoid iOS issues
const injectKeyframes = () => {
  if (typeof document !== 'undefined' && !document.getElementById('spin-keyframes')) {
    const style = document.createElement('style');
    style.id = 'spin-keyframes';
    style.textContent = `
      @keyframes spin {
        0% { transform: rotate(0deg); }
        100% { transform: rotate(360deg); }
      }
    `;
    document.head.appendChild(style);
  }
};

const GroupInviteRedirect = () => {
  const [searchParams] = useSearchParams();
  const [code, setCode] = useState('');
  const [status, setStatus] = useState('loading');

  useEffect(() => {
    // Inject keyframes on mount
    injectKeyframes();

    const codeParam = searchParams.get('code');

    if (codeParam) {
      setCode(codeParam);

      // Deep Link zur App - nur mit Code (wie bei link-child)
      const appDeepLink = `erledix://join-group?code=${codeParam}`;
      window.location.href = appDeepLink;

      setTimeout(() => {
        setStatus('manual');
      }, 2000);
    } else {
      setStatus('error');
    }
  }, [searchParams]);

  const copyToClipboard = () => {
    navigator.clipboard.writeText(code);
  };

  return (
    <div style={styles.body}>
      <div style={styles.container}>
        <div style={styles.logo}>👥</div>
        <h1 style={styles.h1}>Zur Gruppe eingeladen</h1>
        
        <div style={styles.content}>
          {status === 'loading' && (
            <>
              <div style={styles.spinner}></div>
              <p style={styles.message}>App wird geöffnet...</p>
            </>
          )}
          
          {status === 'error' && (
            <div style={styles.statusError}>
              <p>❌ Kein Code gefunden</p>
              <p style={{ marginTop: '10px', fontSize: '14px' }}>
                Bitte verwende den Link aus der Einladung.
              </p>
            </div>
          )}
          
          {status === 'manual' && (
            <>
              <div style={styles.codeBox}>
                <p style={styles.codeLabel}>Dein Einladungscode:</p>
                <div style={styles.code}>{code}</div>
                <button style={styles.copyButton} onClick={copyToClipboard}>
                  📋 Code kopieren
                </button>
              </div>
              
              <p style={styles.message}>Falls die App nicht automatisch geöffnet wurde:</p>
              
              <a 
                href={`erledix://join-group?code=${code}`} 
                style={styles.button}
              >
                App öffnen
              </a>
              
              <div style={styles.instructions}>
                <p style={styles.instructionsTitle}>Oder manuell eingeben:</p>
                <ol style={styles.instructionsList}>
                  <li>Öffne die Erledix App</li>
                  <li>Gehe zu "Gruppen"</li>
                  <li>Tippe auf "Gruppe beitreten"</li>
                  <li>Gib den Code ein: <strong>{code}</strong></li>
                </ol>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

const styles: StylesType = {
  body: {
    margin: 0,
    padding: 0,
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
    backgroundColor: '#f5f5f5',
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  container: {
    maxWidth: '500px',
    width: '90%',
    backgroundColor: 'white',
    borderRadius: '16px',
    padding: '40px',
    boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
    textAlign: 'center' as const,
  },
  logo: {
    fontSize: '64px',
    marginBottom: '20px',
  },
  h1: {
    fontSize: '28px',
    fontWeight: '700',
    color: '#333',
    marginBottom: '30px',
  },
  content: {
    marginTop: '30px',
  },
  spinner: {
    width: '50px',
    height: '50px',
    border: '4px solid #f3f3f3',
    borderTop: '4px solid #667eea',
    borderRadius: '50%',
    margin: '0 auto 20px',
    animation: 'spin 1s linear infinite',
  },
  message: {
    fontSize: '16px',
    color: '#666',
    marginBottom: '20px',
  },
  codeBox: {
    backgroundColor: '#f8f9fa',
    padding: '20px',
    borderRadius: '12px',
    marginBottom: '30px',
  },
  codeLabel: {
    fontSize: '14px',
    color: '#666',
    marginBottom: '10px',
  },
  code: {
    fontSize: '32px',
    fontWeight: '700',
    color: '#667eea',
    letterSpacing: '4px',
    fontFamily: 'monospace',
    marginBottom: '15px',
  },
  copyButton: {
    padding: '10px 20px',
    backgroundColor: '#e8eef7',
    color: '#667eea',
    border: '1px solid #667eea',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.3s',
  },
  button: {
    display: 'inline-block',
    padding: '16px 32px',
    backgroundColor: '#667eea',
    color: 'white',
    textDecoration: 'none',
    borderRadius: '12px',
    fontSize: '16px',
    fontWeight: '600',
    marginTop: '10px',
    transition: 'background-color 0.3s',
  },
  instructions: {
    marginTop: '40px',
    padding: '20px',
    backgroundColor: '#f8f9fa',
    borderRadius: '12px',
    textAlign: 'left' as const,
  },
  instructionsTitle: {
    fontSize: '16px',
    fontWeight: '600',
    color: '#333',
    marginBottom: '15px',
  },
  instructionsList: {
    fontSize: '14px',
    color: '#666',
    lineHeight: '1.8',
    paddingLeft: '20px',
  },
  statusError: {
    padding: '20px',
    backgroundColor: '#fee',
    borderRadius: '12px',
    color: '#c33',
  },
};

export default GroupInviteRedirect;